import { useState } from 'react';
import { Button, Icon, Modal, Tag } from '@neshane/blueberry';
import { useToast } from '../../../common/hooks';
import { useMaintenanceActions, useMaintenanceStatus } from '../hooks';

/**
 * Admin settings card for toggling the global maintenance mode.
 * Shown on the admin settings page.
 */
export const MaintenanceSettingsCard = () => {
    const { enabled, loading: statusLoading } = useMaintenanceStatus();
    const { setMaintenance, loading: actionLoading } = useMaintenanceActions();
    const { success, error } = useToast();

    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    const handleConfirm = async () => {
        const nextEnabled = !enabled;
        const result = await setMaintenance(nextEnabled);
        setIsConfirmOpen(false);
        if (result.success) {
            success(nextEnabled ? 'حالت تعمیرات فعال شد' : 'حالت تعمیرات غیرفعال شد');
        } else {
            error(result.error);
        }
    };

    return (
        <div className="flex w-full justify-center">
            <div className="flex w-full max-w-lg flex-col gap-16">
                <section
                    aria-labelledby="maintenanceModeSetting"
                    className="border border-solid border-default rounded-12 bg-primary-default p-24"
                >
                    <div className="flex flex-wrap items-center gap-16">
                        <span className="flex h-48 w-48 flex-shrink-0 items-center justify-center rounded-12 bg-secondary-default">
                            <Icon name="wrench" size="l" />
                        </span>
                        <div className="flex min-w-0 flex-1 flex-col gap-4">
                            <h2 id="maintenanceModeSetting" className="title-xl text-primary">
                                حالت تعمیرات
                            </h2>
                            <p className="body-s text-secondary-bg-primary">
                                وقتی فعال باشد، کاربران عادی صفحه تعمیرات را می‌بینند و مدیران/اپراتورها دسترسی دارند.
                            </p>
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-16">
                            {enabled ? (
                                <Tag variant="danger">فعال</Tag>
                            ) : (
                                <Tag variant="success">غیرفعال</Tag>
                            )}
                            <Button
                                text={enabled ? 'غیرفعال کردن' : 'فعال کردن'}
                                iconName={enabled ? 'circle-pause' : 'shield-star'}
                                variant={enabled ? 'secondaryAlert' : 'primaryBrand'}
                                onClick={() => setIsConfirmOpen(true)}
                                isDisabled={statusLoading || actionLoading}
                            />
                        </div>
                    </div>

                    {isConfirmOpen && (
                        <Modal
                            isOpen
                            onClose={() => setIsConfirmOpen(false)}
                            iconName="triangle-warning"
                            iconVariant="warning"
                            title={enabled ? 'غیرفعال کردن حالت تعمیرات' : 'فعال کردن حالت تعمیرات'}
                            actions={[
                                <Button
                                    key="onCancel"
                                    isDisabled={actionLoading}
                                    onClick={() => setIsConfirmOpen(false)}
                                    variant="secondaryNeutral"
                                    text="انصراف"
                                />,
                                <Button
                                    key="onConfirm"
                                    onClick={handleConfirm}
                                    text={enabled ? 'غیرفعال کردن' : 'فعال کردن'}
                                    isDisabled={actionLoading}
                                />,
                            ]}
                        >
                            {enabled
                                ? 'با غیرفعال کردن حالت تعمیرات، کاربران عادی دوباره به سرویس دسترسی خواهند داشت. ادامه می‌دهید؟'
                                : 'با فعال کردن حالت تعمیرات، کاربران عادی به جای سرویس، صفحه تعمیرات را می‌بینند. ادامه می‌دهید؟'}
                        </Modal>
                    )}
                </section>
            </div>
        </div>
    );
};

export default MaintenanceSettingsCard;
